import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getProgressSummary, getSummary } from "../../api/process";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import WidgetCard from "./WidgetCard";
import { Settings } from "lucide-react";
import { createPortal } from "react-dom";
import { updateProjectSafeDate } from "../../api/project";



ChartJS.register(ArcElement, Tooltip, Legend);

export default function DDayCounter() {
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [baselineProgress, setBaselineProgress] = useState(0);
  const [actualProgress, setActualProgress] = useState(0);
  const { id: projectId } = useParams();
  const [safeStartDate, setSafeStartDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });
  const settingsRef = useRef(null);


  const today = new Date();
  const safeStart = new Date(safeStartDate);

  // 하루 단위 차이 계산
  const safeDays = Math.max(
    Math.floor((today - safeStart) / (1000 * 60 * 60 * 24)),
    0
  );


  useEffect(() => {
    if (!showCalendar) return;

    function handleClickOutside(e) {
      // 달력 DOM을 클릭한 게 아니라면 닫기
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  const calendarRef = useRef(null);


  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await getSummary(projectId);
        const progress = await getProgressSummary();
        console.log(data);
        setStartDate(data.period_start);
        setEndDate(data.period_finish);
        setSafeStartDate(data.safe_date || "");
        setBaselineProgress(
          progress.project.summary.baseline_progress_rate || 0
        );
        setActualProgress(progress.project.summary.actual_progress_rate || 0);
      } catch (error) {
        console.error("프로젝트 요약 데이터 불러오기 실패:", error);
      }
    };
    fetchSummary();
  }, [projectId]);

  if (!startDate || !endDate) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 w-full text-center text-slate-500">
        프로젝트 기간 정보를 불러오는 중입니다...
      </div>
    );
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.max(
    Math.floor((end - start) / (1000 * 60 * 60 * 24)),
    1
  );
  const passedDays = Math.max(
    Math.floor((today - start) / (1000 * 60 * 60 * 24)),
    0
  );
  const remainingDays = Math.max(totalDays - passedDays, 0);

  // ✅ 도넛 그래프 데이터
  const chartData = {
    datasets: [
      {
        // 실제 진도율
        data: [actualProgress, 100 - actualProgress],
        backgroundColor: ["#2563eb", "#e5e7eb"],
        borderWidth: 0,
        cutout: "75%",
        radius: "100%",
      },
      {
        // 베이스라인 (얇은 링)
        data: [baselineProgress, 100 - baselineProgress],
        backgroundColor: ["#18d100ff", "transparent"],
        borderWidth: 0,
        cutout: "88%",
        radius: "88%",
      },
    ],
  };

  const chartOptions = {
    plugins: {
      animation: false,
      legend: { display: false }, //  범례 제거
      tooltip: { enabled: false }, //  툴팁 제거
      datalabels: { display: false }, //  도넛 조각 위 자동 숫자 제거
    },
  };

  // ✅ 중앙 텍스트 표시 플러그인
  const centerTextPlugin = {
    id: "centerText",
    beforeDraw: (chart) => {
      const { width, height, ctx } = chart;
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // 실제 진행률 (큰 글씨)
      ctx.font = "700 18px Pretendard, sans-serif";
      ctx.fillStyle = "#2563eb";
      ctx.fillText(`${actualProgress.toFixed(1)}%`, width / 2, height / 2 - 5);

      // 베이스라인 (작은 글씨)
      ctx.font = "500 10px Pretendard, sans-serif";
      ctx.fillStyle = "#18d100ff";
      ctx.fillText(
        `Baseline: ${baselineProgress.toFixed(1)}%`,
        width / 2,
        height / 2 + 12
      );

      ctx.restore();
    },
  };

  return (
    <WidgetCard title="프로젝트 진행 현황">
      <div className="flex flex-row items-center justify-center gap-6">

        {/* 🔵 D-DAY 영역 */}
        <div className="text-left flex-shrink-0">
          <p className="text-slate-600 font-medium">
            Today {today.getFullYear()}.{" "}
            {String(today.getMonth() + 1).padStart(2, "0")}.{" "}
            {String(today.getDate()).padStart(2, "0")}.
          </p>

          <p className="text-xs text-slate-500 mt-1">프로젝트 종료까지</p>

          <p className="text-5xl font-extrabold text-blue-700 mt-1 mb-1">
            D-{remainingDays}
          </p>

          <p className="text-xs text-slate-500">
            (총 {totalDays}일 중 {passedDays}일 경과)
          </p>

          <p className="text-[11px] text-slate-400 mt-1">
            {startDate} ~ {endDate}
          </p>
          {safeStartDate &&
            <div className="mt-3">
              <p className="text-sm text-green-600 font-semibold">
                무사고 {safeDays}일
              </p>

              <div className="flex items-center gap-1 text-[11px] text-slate-400 relative">
                <span>
                  ({safeStartDate.replace(/-/g, ".")} ~ 현재)
                </span>

                {/* 세팅 버튼 */}
                <button
                  ref={settingsRef}
                  onClick={() => {
                    if (settingsRef.current) {
                      const rect = settingsRef.current.getBoundingClientRect();
                      setCalendarPos({
                        top: rect.bottom + 6,   // 버튼 아래로 6px
                        left: rect.left - 20,   // 약간 왼쪽으로 조절
                      });
                    }
                    setShowCalendar(true);
                  }}
                  className="ml-1 p-1 hover:bg-slate-100 rounded"
                >
                  <Settings size={12} className="text-slate-500" />
                </button>
                {showCalendar &&
                  createPortal(
                    <div
                      ref={calendarRef}
                      className="absolute bg-white border rounded-md shadow p-3 w-40 z-[9999]"
                      style={{
                        position: "absolute",
                        top: calendarPos.top,
                        left: calendarPos.left,
                      }}
                    >
                      <label className="text-xs text-slate-600">시작일 설정</label>
                      <input
                        type="date"
                        value={safeStartDate}
                        onChange={async (e) => {
                          const newDate = e.target.value;

                          await updateProjectSafeDate(newDate);
                          setSafeStartDate(newDate);
                          setShowCalendar(false);   // 날짜 선택하면 닫힘
                        }}
                        className="border p-1 rounded mt-1 w-full text-xs"
                      />

                    </div>,
                    document.body
                  )
                }

              </div>
            </div>
          }
        </div>

        {/* 🔵 도넛 그래프 */}
        <div className="flex flex-col items-center">
          <div className="relative w-[130px] h-[130px]">
            <Doughnut
              data={chartData}
              options={chartOptions}
              plugins={[centerTextPlugin]}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">공정 진행률</p>
        </div>
      </div>
    </WidgetCard>


  );

}
